// Prospera eSign — envelope list + entry point for creating new envelopes.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileSignature, Plus, ShieldCheck, Clock, CheckCircle2, XCircle, Copy, Search } from "lucide-react";
import api from "./api";

const STATUS_META = {
  draft:       { label: "Draft",        color: "#8e8e93" },
  sent:        { label: "Sent",         color: "#0071e3" },
  in_progress: { label: "In progress",  color: "#ff9f0a" },
  completed:   { label: "Completed",    color: "#30d158" },
  declined:    { label: "Declined",     color: "#ff453a" },
  voided:      { label: "Voided",       color: "#636366" },
};

export default function Sign() {
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [pk, setPk] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/sign/envelopes", { params: { q: q || undefined, status: status || undefined } })
      .then((r) => setEnvelopes(r.data.items || [])).finally(() => setLoading(false));
  }, [q, status]);

  useEffect(() => { api.get("/sign/public-key").then((r) => setPk(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="sign-page" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-[34px] tracking-tight leading-none"
            style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.02em" }}>
            Sign
          </h1>
          <div className="mt-2 text-[14px]" style={{ color: "var(--crm-text-muted)" }}>
            Cryptographically-sealed e-signatures · Ed25519 · SHA-256 audit chain
          </div>
        </div>
        <button
          onClick={() => nav("/crm/sign/new")}
          data-testid="new-envelope-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium text-black"
          style={{ background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)" }}
        >
          <Plus size={14} /> New envelope
        </button>
      </header>

      {/* Trust strip */}
      {pk && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-2xl text-[12.5px]"
          style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)", color: "var(--crm-text-muted)" }}
          data-testid="crypto-strip"
        >
          <ShieldCheck size={16} style={{ color: "var(--crm-accent)" }} />
          <div className="flex-1">
            <span style={{ color: "var(--crm-text)", fontWeight: 500 }}>{pk.algorithm}</span>
            {" · "}
            <span className="font-mono text-[11px]">fingerprint {pk.fingerprint.slice(0, 24)}…</span>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(pk.fingerprint); }}
            className="inline-flex items-center gap-1 text-[11.5px]"
            style={{ color: "var(--crm-accent)" }}
            data-testid="copy-pk-fingerprint"
          >
            <Copy size={11} /> copy fingerprint
          </button>
        </div>
      )}

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--crm-text-subtle)" }} />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject, document, or envelope ID…"
            data-testid="sign-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg text-[13px] focus:outline-none"
            style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}
          />
        </div>
        <select
          value={status} onChange={(e) => setStatus(e.target.value)}
          data-testid="sign-status-filter"
          className="px-3 py-2 rounded-lg text-[13px] focus:outline-none"
          style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)", minWidth: 160 }}
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="rounded-2xl overflow-hidden"
        style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)", boxShadow: "var(--crm-shadow)" }}>
        <table className="w-full text-[13px]">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--crm-line-soft)" }}>
              <Th>Subject</Th>
              <Th className="hidden md:table-cell">Document</Th>
              <Th className="hidden lg:table-cell">Signers</Th>
              <Th className="hidden md:table-cell">Status</Th>
              <Th className="hidden lg:table-cell">Sent</Th>
              <Th className="hidden xl:table-cell">Completed</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-10 text-center" style={{ color: "var(--crm-text-subtle)" }}>Loading…</td></tr>
            ) : envelopes.length === 0 ? (
              <tr><td colSpan={6} className="p-16 text-center" style={{ color: "var(--crm-text-subtle)" }}>
                <FileSignature size={36} className="mx-auto mb-3 opacity-40" />
                <div className="text-[15px]" style={{ color: "var(--crm-text-muted)" }}>No envelopes yet.</div>
                <div className="text-[12.5px] mt-1">Send your first document to a client for signature.</div>
                <Link to="/crm/sign/new" data-testid="empty-new-btn"
                  className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium text-black"
                  style={{ background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)" }}>
                  <Plus size={14} /> New envelope
                </Link>
              </td></tr>
            ) : envelopes.map((e) => <EnvRow key={e.id} e={e} nav={nav} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EnvRow({ e, nav }) {
  const meta = STATUS_META[e.status] || STATUS_META.draft;
  const doneCount = (e.signers || []).filter((s) => s.status === "signed").length;
  const totalCount = (e.signers || []).length;
  return (
    <tr onClick={() => nav(`/crm/sign/${e.id}`)}
      onMouseEnter={(ev) => (ev.currentTarget.style.background = "var(--crm-hover)")}
      onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
      data-testid={`envelope-row-${e.id}`}
      style={{ borderBottom: "1px solid var(--crm-line-soft)", cursor: "pointer", transition: "background 0.15s" }}>
      <Td>
        <div className="min-w-0">
          <div className="truncate" style={{ color: "var(--crm-text)", fontWeight: 500 }}>{e.subject}</div>
          <div className="text-[11.5px] truncate font-mono" style={{ color: "var(--crm-text-subtle)" }}>{e.id.slice(0, 8)}…{e.doc_hash?.slice(0, 8)}</div>
        </div>
      </Td>
      <Td className="hidden md:table-cell text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
        <span className="truncate block max-w-[220px]">{e.doc_name}</span>
      </Td>
      <Td className="hidden lg:table-cell text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
        {totalCount ? `${doneCount}/${totalCount} signed` : "—"}
      </Td>
      <Td className="hidden md:table-cell text-[12px]">
        <span className="inline-flex items-center gap-1.5" style={{ color: meta.color }}>
          <StatusIcon status={e.status} />
          {meta.label}
        </span>
      </Td>
      <Td className="hidden lg:table-cell text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
        {e.sent_at ? new Date(e.sent_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—"}
      </Td>
      <Td className="hidden xl:table-cell text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
        {e.completed_at ? new Date(e.completed_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—"}
      </Td>
    </tr>
  );
}

function StatusIcon({ status }) {
  const size = 12;
  if (status === "completed") return <CheckCircle2 size={size} />;
  if (status === "declined" || status === "voided") return <XCircle size={size} />;
  return <Clock size={size} />;
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-[10.5px] uppercase tracking-[0.08em] font-normal ${className}`}
    style={{ color: "var(--crm-text-subtle)" }}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>
);
